/**
 * Billetterie — types de billets, quotas, codes promo, stands, commandes et
 * scan d'entrée. Un seul écran, cinq onglets : la billetterie d'un événement
 * se pense comme un tout, pas comme cinq écrans séparés à retrouver.
 */
import { useEffect, useState } from 'react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, Chip, eur, Input, Modal, PageTitle, Select, Table, TextArea } from '../ui.tsx';
import type { EvenementAdmin } from '../EspaceAdmin.tsx';

type Onglet = 'billets' | 'codes' | 'stands' | 'commandes' | 'scanner';

export function Billetterie({
  evenements,
  organisationSlug,
}: {
  evenements: EvenementAdmin[];
  organisationSlug: string;
}) {
  const [evenementId, setEvenementId] = useState(evenements[0]?.id ?? '');
  const [onglet, setOnglet] = useState<Onglet>('billets');
  const evenementSelectionne = evenements.find((e) => e.id === evenementId);

  if (!evenementId) {
    return (
      <PageTitle
        title="Billetterie"
        subtitle="Créez d'abord un événement (onglet « Mes événements ») pour ouvrir sa billetterie."
      />
    );
  }

  return (
    <>
      <PageTitle
        title="Billetterie"
        subtitle="Types de billets, quotas partagés, codes promo, stands d'exposants, ventes et scan d'entrée."
        action={
          evenements.length > 1 ? (
            <Select
              label=""
              value={evenementId}
              onChange={setEvenementId}
              options={evenements.map((e) => ({ valeur: e.id, texte: e.nom }))}
            />
          ) : undefined
        }
      />

      <div className="mb-6 flex flex-wrap gap-2">
        {(
          [
            ['billets', 'Types de billets'],
            ['codes', 'Codes promo'],
            ['stands', 'Stands'],
            ['commandes', 'Commandes'],
            ['scanner', "Scan d'entrée"],
          ] as [Onglet, string][]
        ).map(([cle, texte]) => (
          <Chip key={cle} active={onglet === cle} onClick={() => setOnglet(cle)}>
            {texte}
          </Chip>
        ))}
      </div>

      {onglet === 'billets' && (
        <OngletBillets
          evenementId={evenementId}
          lienBoutique={
            evenementSelectionne ? `${location.origin}/billet/${organisationSlug}/${evenementSelectionne.slug}` : ''
          }
        />
      )}
      {onglet === 'codes' && <OngletCodes evenementId={evenementId} />}
      {onglet === 'stands' && <OngletStands evenementId={evenementId} />}
      {onglet === 'commandes' && <OngletCommandes evenementId={evenementId} />}
      {onglet === 'scanner' && <OngletScanner evenementId={evenementId} />}
    </>
  );
}

// -----------------------------------------------------------------------------
// Types de billets + quotas
// -----------------------------------------------------------------------------

type Quota = { id: string; nom: string; capacite: number; vendus: number };
type TypeBillet = {
  id: string;
  nom: string;
  description: string | null;
  prix_centimes: number;
  quota_id: string | null;
  quota_nom: string | null;
  quota_capacite: number | null;
  visible: boolean;
  actif: boolean;
  vendus: number;
};

type BrouillonType = {
  nom: string;
  description: string;
  prix: string;
  quotaId: string;
  visible: boolean;
};
const TYPE_VIDE: BrouillonType = { nom: '', description: '', prix: '0', quotaId: '', visible: true };

function OngletBillets({ evenementId, lienBoutique }: { evenementId: string; lienBoutique: string }) {
  const [quotas, setQuotas] = useState<Quota[]>([]);
  const [types, setTypes] = useState<TypeBillet[]>([]);
  const [editionType, setEditionType] = useState<{ id: string | null; v: BrouillonType } | null>(null);
  const [editionQuota, setEditionQuota] = useState<{ nom: string; capacite: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = async () => {
    const [q, t] = await Promise.all([
      appelerApi<{ quotas: Quota[] }>(`/api/billetterie/evenements/${evenementId}/quotas`),
      appelerApi<{ types: TypeBillet[] }>(`/api/billetterie/evenements/${evenementId}/types`),
    ]);
    if (q.donnees) setQuotas(q.donnees.quotas);
    if (t.donnees) setTypes(t.donnees.types);
  };

  useEffect(() => {
    void charger();
  }, [evenementId]);

  const enregistrerQuota = async () => {
    if (!editionQuota) return;
    const r = await envoyer(`/api/billetterie/evenements/${evenementId}/quotas`, {
      nom: editionQuota.nom,
      capacite: Number(editionQuota.capacite),
    });
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    setEditionQuota(null);
    void charger();
  };

  const enregistrerType = async () => {
    if (!editionType) return;
    const corps = {
      nom: editionType.v.nom,
      description: editionType.v.description || null,
      prixCentimes: Math.round(parseFloat(editionType.v.prix.replace(',', '.') || '0') * 100),
      quotaId: editionType.v.quotaId || null,
      visible: editionType.v.visible,
    };
    const r = editionType.id
      ? await envoyer(`/api/billetterie/evenements/${evenementId}/types/${editionType.id}`, corps, 'PUT')
      : await envoyer(`/api/billetterie/evenements/${evenementId}/types`, corps);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    setErreur(null);
    setEditionType(null);
    void charger();
  };

  const basculerVisibilite = async (t: TypeBillet) => {
    await envoyer(`/api/billetterie/evenements/${evenementId}/types/${t.id}`, { visible: !t.visible }, 'PUT');
    void charger();
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[15px] text-blanc">Quotas (capacité partagée entre plusieurs types)</p>
          <Btn onClick={() => setEditionQuota({ nom: '', capacite: '100' })}>Nouveau quota</Btn>
        </div>
        {quotas.length === 0 ? (
          <p className="mt-3 text-[13px] text-fumee">
            Aucun quota — un type de billet sans quota est en capacité illimitée.
          </p>
        ) : (
          <ul className="mt-3 flex flex-col gap-2">
            {quotas.map((q) => (
              <li key={q.id} className="flex items-center justify-between text-[13px] text-fumee">
                <span>{q.nom}</span>
                <span>
                  {q.vendus} / {q.capacite}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {lienBoutique && (
          <div className="flex min-w-0 items-center gap-2">
            <p className="text-[13px] text-fumee">Page de vente publique :</p>
            <code className="truncate rounded-[6px] border border-graphite px-2 py-1 text-[12px] text-blanc">
              {lienBoutique}
            </code>
            <button
              className="text-[13px] text-fumee hover:text-blanc"
              onClick={() => void navigator.clipboard.writeText(lienBoutique)}
            >
              Copier
            </button>
          </div>
        )}
        <Btn variant="primary" onClick={() => setEditionType({ id: null, v: { ...TYPE_VIDE, quotaId: quotas[0]?.id ?? '' } })}>
          Nouveau type de billet
        </Btn>
      </div>

      <Table head={['Type', 'Prix', 'Quota', 'Vendus', 'Visible', '']}>
        {types.map((t) => (
          <tr key={t.id}>
            <td className="text-blanc">{t.nom}</td>
            <td className="text-fumee">{t.prix_centimes === 0 ? 'Gratuit' : eur(t.prix_centimes / 100)}</td>
            <td className="text-fumee">{t.quota_nom ?? 'Illimité'}</td>
            <td className="text-fumee">{t.vendus}</td>
            <td>
              <button onClick={() => void basculerVisibilite(t)}>
                <Badge filled={t.visible}>{t.visible ? 'Visible' : 'Masqué'}</Badge>
              </button>
            </td>
            <td>
              <button
                className="text-[13px] text-fumee hover:text-blanc"
                onClick={() =>
                  setEditionType({
                    id: t.id,
                    v: {
                      nom: t.nom,
                      description: t.description ?? '',
                      prix: (t.prix_centimes / 100).toFixed(2),
                      quotaId: t.quota_id ?? '',
                      visible: t.visible,
                    },
                  })
                }
              >
                Modifier
              </button>
            </td>
          </tr>
        ))}
        {types.length === 0 && (
          <tr>
            <td colSpan={6} className="text-fumee">
              Aucun type de billet.
            </td>
          </tr>
        )}
      </Table>

      {editionQuota && (
        <Modal title="Nouveau quota" onClose={() => setEditionQuota(null)}>
          <div className="flex flex-col gap-4">
            <Input label="Nom" value={editionQuota.nom} onChange={(v) => setEditionQuota({ ...editionQuota, nom: v })} placeholder="Places générales" />
            <Input label="Capacité" value={editionQuota.capacite} onChange={(v) => setEditionQuota({ ...editionQuota, capacite: v })} placeholder="200" />
            {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
            <div className="flex justify-end gap-3">
              <Btn onClick={() => setEditionQuota(null)}>Annuler</Btn>
              <Btn variant="primary" onClick={() => void enregistrerQuota()}>Enregistrer</Btn>
            </div>
          </div>
        </Modal>
      )}

      {editionType && (
        <Modal title={editionType.id ? 'Modifier le billet' : 'Nouveau type de billet'} onClose={() => setEditionType(null)}>
          <div className="flex flex-col gap-4">
            <Input label="Nom" value={editionType.v.nom} onChange={(v) => setEditionType({ ...editionType, v: { ...editionType.v, nom: v } })} placeholder="Billet standard" />
            <TextArea label="Description" value={editionType.v.description} onChange={(v) => setEditionType({ ...editionType, v: { ...editionType.v, description: v } })} rows={2} />
            <Input label="Prix (€, 0 = gratuit)" value={editionType.v.prix} onChange={(v) => setEditionType({ ...editionType, v: { ...editionType.v, prix: v } })} placeholder="25.00" />
            <Select
              label="Quota"
              value={editionType.v.quotaId}
              onChange={(v) => setEditionType({ ...editionType, v: { ...editionType.v, quotaId: v } })}
              options={[{ valeur: '', texte: 'Illimité' }, ...quotas.map((q) => ({ valeur: q.id, texte: q.nom }))]}
            />
            {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
            <div className="flex justify-end gap-3">
              <Btn onClick={() => setEditionType(null)}>Annuler</Btn>
              <Btn variant="primary" onClick={() => void enregistrerType()}>Enregistrer</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Codes promo
// -----------------------------------------------------------------------------

type CodePromo = {
  id: string;
  code: string;
  type: 'pourcentage' | 'montant';
  valeur: number;
  usage_max: number | null;
  usage_actuel: number;
  actif: boolean;
};

function OngletCodes({ evenementId }: { evenementId: string }) {
  const [codes, setCodes] = useState<CodePromo[]>([]);
  const [edition, setEdition] = useState<{ code: string; type: 'pourcentage' | 'montant'; valeur: string; usageMax: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = async () => {
    const r = await appelerApi<{ codes: CodePromo[] }>(`/api/billetterie/evenements/${evenementId}/codes-promo`);
    if (r.donnees) setCodes(r.donnees.codes);
  };
  useEffect(() => {
    void charger();
  }, [evenementId]);

  const enregistrer = async () => {
    if (!edition) return;
    const r = await envoyer(`/api/billetterie/evenements/${evenementId}/codes-promo`, {
      code: edition.code,
      type: edition.type,
      valeur: Number(edition.valeur.replace(',', '.')),
      usageMax: edition.usageMax ? Number(edition.usageMax) : undefined,
    });
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    setErreur(null);
    setEdition(null);
    void charger();
  };

  const basculer = async (c: CodePromo) => {
    await envoyer(`/api/billetterie/evenements/${evenementId}/codes-promo/${c.id}`, { actif: !c.actif }, 'PUT');
    void charger();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Btn variant="primary" onClick={() => setEdition({ code: '', type: 'pourcentage', valeur: '10', usageMax: '' })}>
          Nouveau code promo
        </Btn>
      </div>
      <Table head={['Code', 'Remise', 'Utilisations', 'Actif', '']}>
        {codes.map((c) => (
          <tr key={c.id}>
            <td className="text-blanc">{c.code}</td>
            <td className="text-fumee">{c.type === 'pourcentage' ? `${c.valeur} %` : eur(c.valeur)}</td>
            <td className="text-fumee">
              {c.usage_actuel}
              {c.usage_max ? ` / ${c.usage_max}` : ''}
            </td>
            <td>
              <button onClick={() => void basculer(c)}>
                <Badge filled={c.actif}>{c.actif ? 'Actif' : 'Inactif'}</Badge>
              </button>
            </td>
            <td />
          </tr>
        ))}
        {codes.length === 0 && (
          <tr>
            <td colSpan={5} className="text-fumee">
              Aucun code promo.
            </td>
          </tr>
        )}
      </Table>

      {edition && (
        <Modal title="Nouveau code promo" onClose={() => setEdition(null)}>
          <div className="flex flex-col gap-4">
            <Input label="Code" value={edition.code} onChange={(v) => setEdition({ ...edition, code: v.toUpperCase() })} placeholder="EARLYBIRD" />
            <Select
              label="Type de remise"
              value={edition.type}
              onChange={(v) => setEdition({ ...edition, type: v as 'pourcentage' | 'montant' })}
              options={[{ valeur: 'pourcentage', texte: 'Pourcentage' }, { valeur: 'montant', texte: 'Montant fixe (€)' }]}
            />
            <Input label={edition.type === 'pourcentage' ? 'Valeur (%)' : 'Valeur (€)'} value={edition.valeur} onChange={(v) => setEdition({ ...edition, valeur: v })} />
            <Input label="Utilisations maximum (optionnel)" value={edition.usageMax} onChange={(v) => setEdition({ ...edition, usageMax: v })} placeholder="Illimité" />
            {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
            <div className="flex justify-end gap-3">
              <Btn onClick={() => setEdition(null)}>Annuler</Btn>
              <Btn variant="primary" onClick={() => void enregistrer()}>Enregistrer</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Stands
// -----------------------------------------------------------------------------

type Stand = {
  id: string;
  nom: string;
  entreprise: string | null;
  emplacement: string | null;
  statut: 'propose' | 'confirme' | 'annule';
  prix_centimes: number | null;
};

function OngletStands({ evenementId }: { evenementId: string }) {
  const [stands, setStands] = useState<Stand[]>([]);
  const [edition, setEdition] = useState<{ id: string | null; nom: string; entreprise: string; emplacement: string; prix: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = async () => {
    const r = await appelerApi<{ stands: Stand[] }>(`/api/billetterie/evenements/${evenementId}/stands`);
    if (r.donnees) setStands(r.donnees.stands);
  };
  useEffect(() => {
    void charger();
  }, [evenementId]);

  const enregistrer = async () => {
    if (!edition) return;
    const corps = {
      nom: edition.nom,
      entreprise: edition.entreprise || null,
      emplacement: edition.emplacement || null,
      prixCentimes: edition.prix ? Math.round(parseFloat(edition.prix.replace(',', '.')) * 100) : null,
    };
    const r = edition.id
      ? await envoyer(`/api/billetterie/evenements/${evenementId}/stands/${edition.id}`, corps, 'PUT')
      : await envoyer(`/api/billetterie/evenements/${evenementId}/stands`, corps);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    setErreur(null);
    setEdition(null);
    void charger();
  };

  const changerStatut = async (s: Stand, statut: Stand['statut']) => {
    await envoyer(`/api/billetterie/evenements/${evenementId}/stands/${s.id}`, { statut }, 'PUT');
    void charger();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Btn variant="primary" onClick={() => setEdition({ id: null, nom: '', entreprise: '', emplacement: '', prix: '' })}>
          Nouveau stand
        </Btn>
      </div>
      <Table head={['Stand', 'Entreprise', 'Emplacement', 'Prix', 'Statut', '']}>
        {stands.map((s) => (
          <tr key={s.id}>
            <td className="text-blanc">{s.nom}</td>
            <td className="text-fumee">{s.entreprise ?? '—'}</td>
            <td className="text-fumee">{s.emplacement ?? '—'}</td>
            <td className="text-fumee">{s.prix_centimes ? eur(s.prix_centimes / 100) : '—'}</td>
            <td>
              <select
                value={s.statut}
                onChange={(e) => void changerStatut(s, e.target.value as Stand['statut'])}
                className="field-input !min-h-[32px] !w-auto !py-1 !text-xs"
              >
                <option value="propose">Proposé</option>
                <option value="confirme">Confirmé</option>
                <option value="annule">Annulé</option>
              </select>
            </td>
            <td>
              <button
                className="text-[13px] text-fumee hover:text-blanc"
                onClick={() =>
                  setEdition({
                    id: s.id,
                    nom: s.nom,
                    entreprise: s.entreprise ?? '',
                    emplacement: s.emplacement ?? '',
                    prix: s.prix_centimes ? (s.prix_centimes / 100).toFixed(2) : '',
                  })
                }
              >
                Modifier
              </button>
            </td>
          </tr>
        ))}
        {stands.length === 0 && (
          <tr>
            <td colSpan={6} className="text-fumee">
              Aucun stand.
            </td>
          </tr>
        )}
      </Table>

      {edition && (
        <Modal title={edition.id ? 'Modifier le stand' : 'Nouveau stand'} onClose={() => setEdition(null)}>
          <div className="flex flex-col gap-4">
            <Input label="Nom du stand" value={edition.nom} onChange={(v) => setEdition({ ...edition, nom: v })} placeholder="Stand A12" />
            <Input label="Entreprise exposante" value={edition.entreprise} onChange={(v) => setEdition({ ...edition, entreprise: v })} />
            <Input label="Emplacement" value={edition.emplacement} onChange={(v) => setEdition({ ...edition, emplacement: v })} placeholder="Hall 1, allée B" />
            <Input label="Prix (€, optionnel)" value={edition.prix} onChange={(v) => setEdition({ ...edition, prix: v })} />
            {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
            <div className="flex justify-end gap-3">
              <Btn onClick={() => setEdition(null)}>Annuler</Btn>
              <Btn variant="primary" onClick={() => void enregistrer()}>Enregistrer</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Commandes + statistiques
// -----------------------------------------------------------------------------

type Commande = {
  id: string;
  acheteur_email: string;
  acheteur_prenom: string | null;
  acheteur_nom: string | null;
  total_centimes: number;
  statut: string;
  nb_billets: number;
  cree_le: string;
};
type Stats = { commandes_payees: number; revenu_centimes: number; billets_valides: number; billets_scannes: number };

function OngletCommandes({ evenementId }: { evenementId: string }) {
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    void appelerApi<{ commandes: Commande[] }>(`/api/billetterie/evenements/${evenementId}/commandes`).then((r) => {
      if (r.donnees) setCommandes(r.donnees.commandes);
    });
    void appelerApi<Stats>(`/api/billetterie/evenements/${evenementId}/statistiques`).then((r) => {
      if (r.donnees) setStats(r.donnees);
    });
  }, [evenementId]);

  return (
    <div className="flex flex-col gap-6">
      {stats && (
        <div className="grid gap-4 sm:grid-cols-4">
          <Card><p className="label-caps text-fumee">Commandes payées</p><p className="display-title mt-2 text-[26px] text-blanc">{stats.commandes_payees}</p></Card>
          <Card><p className="label-caps text-fumee">Revenu</p><p className="display-title mt-2 text-[26px] text-blanc">{eur(stats.revenu_centimes / 100)}</p></Card>
          <Card><p className="label-caps text-fumee">Billets valides</p><p className="display-title mt-2 text-[26px] text-blanc">{stats.billets_valides}</p></Card>
          <Card><p className="label-caps text-fumee">Déjà scannés</p><p className="display-title mt-2 text-[26px] text-blanc">{stats.billets_scannes}</p></Card>
        </div>
      )}
      <Table head={['Acheteur', 'Billets', 'Total', 'Statut', 'Date']}>
        {commandes.map((c) => (
          <tr key={c.id}>
            <td className="text-blanc">{[c.acheteur_prenom, c.acheteur_nom].filter(Boolean).join(' ') || c.acheteur_email}</td>
            <td className="text-fumee">{c.nb_billets}</td>
            <td className="text-fumee">{eur(c.total_centimes / 100)}</td>
            <td><Badge filled={c.statut === 'payee'}>{c.statut}</Badge></td>
            <td className="text-fumee">{new Date(c.cree_le).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
          </tr>
        ))}
        {commandes.length === 0 && (
          <tr>
            <td colSpan={5} className="text-fumee">Aucune commande.</td>
          </tr>
        )}
      </Table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Scan d'entrée
// -----------------------------------------------------------------------------

type ResultatScan = {
  resultat: 'accepte' | 'deja_scanne' | 'annule';
  billet: { reference: string | null; type_nom: string; porteur_prenom: string | null; porteur_nom: string | null };
};

function OngletScanner({ evenementId }: { evenementId: string }) {
  const [jeton, setJeton] = useState('');
  const [resultat, setResultat] = useState<ResultatScan | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const scanner = async (valeur: string) => {
    if (!valeur.trim()) return;
    setEnCours(true);
    setErreur(null);
    const r = await envoyer<ResultatScan>(`/api/billetterie/evenements/${evenementId}/scanner`, { jeton: valeur.trim() });
    setEnCours(false);
    if (r.erreur) {
      setErreur(r.erreur);
      setResultat(null);
      return;
    }
    setResultat(r.donnees);
    setJeton('');
  };

  const COULEUR: Record<ResultatScan['resultat'], string> = {
    accepte: 'var(--em-succes, #3ddc84)',
    deja_scanne: 'var(--em-attention, #ffb020)',
    annule: 'var(--em-erreur)',
  };
  const LIBELLE: Record<ResultatScan['resultat'], string> = {
    accepte: '✓ Entrée validée',
    deja_scanne: '⚠ Billet déjà scanné',
    annule: '✕ Billet annulé',
  };

  return (
    <Card className="mx-auto max-w-[480px]">
      <p className="text-[15px] text-blanc">Scanner un billet</p>
      <p className="mt-2 text-[13px] text-fumee">
        Utilisez le lecteur de code-barres de votre appareil (il tape le code puis Entrée) ou saisissez la
        référence manuellement.
      </p>
      <form
        className="mt-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void scanner(jeton);
        }}
      >
        <input
          autoFocus
          value={jeton}
          onChange={(e) => setJeton(e.target.value)}
          placeholder="Coller ou scanner le code du billet"
          className="field-input !min-h-[42px] !py-2 !text-sm"
        />
        <Btn type="submit" variant="primary" disabled={enCours || !jeton.trim()}>
          Vérifier
        </Btn>
      </form>

      {erreur && <p className="mt-4 text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}

      {resultat && (
        <div className="mt-4 rounded-[8px] border border-graphite p-4">
          <p className="text-[15px] font-medium" style={{ color: COULEUR[resultat.resultat] }}>
            {LIBELLE[resultat.resultat]}
          </p>
          <p className="mt-2 text-[14px] text-blanc">
            {[resultat.billet.porteur_prenom, resultat.billet.porteur_nom].filter(Boolean).join(' ') || 'Porteur inconnu'}
          </p>
          <p className="text-[13px] text-fumee">
            {resultat.billet.type_nom} — {resultat.billet.reference}
          </p>
        </div>
      )}
    </Card>
  );
}
